import React, { Suspense } from 'react';
import {
  createBrowserRouter,
  createRoutesFromElements,
  Navigate,
  Route,
  RouterProvider,
} from 'react-router';
import {
  AuthLayout,
  NoAuthLayout,
  AdminAuthLayout,
  AdminNoAuthLayout,
  TenantStaffAuthLayout,
  TenantStaffNoAuthLayout,
} from '../layouts';
import {
  noAuthRoutes,
  authRoutes,
  adminNoAuthRoutes,
  adminAuthRoutes,
  tenantStaffNoAuthRoutes,
  tenantStaffAuthRoutes,
} from './route-config';
import { routes } from './routes';
import { useAuth } from '../hooks';
import { AdminAuthProvider } from '../hooks/use-admin-auth';
import { TenantStaffAuthProvider } from '@/hooks/use-tenant-staff-auth';
import { Loader } from '../components';

export const Routing: React.FC = () => {
  const { loading } = useAuth();

  if (loading) return <Loader />;

  const router = createBrowserRouter(
    createRoutesFromElements(
      <>
        <Route element={<NoAuthLayout />}>
          {noAuthRoutes.map(({ path, element }) => (
            <Route key={path} path={path} element={element} />
          ))}
        </Route>

        <Route element={<AuthLayout />}>
          {authRoutes.map(({ path, element }) => (
            <Route key={path} path={path} element={element} />
          ))}
        </Route>

        {/* Admin routes wrapped with AdminAuthProvider */}
        <Route
          element={
            <AdminAuthProvider>
              <AdminNoAuthLayout />
            </AdminAuthProvider>
          }
        >
          {adminNoAuthRoutes.map(({ path, element }) => (
            <Route key={path} path={path} element={element} />
          ))}
        </Route>

        <Route
          element={
            <AdminAuthProvider>
              <AdminAuthLayout />
            </AdminAuthProvider>
          }
        >
          {adminAuthRoutes.map(({ path, element }) => (
            <Route key={path} path={path} element={element} />
          ))}
        </Route>

        <Route
          element={
            <TenantStaffAuthProvider>
              <TenantStaffNoAuthLayout />
            </TenantStaffAuthProvider>
          }
        >
          {tenantStaffNoAuthRoutes.map(({ path, element }) => (
            <Route key={path} path={path} element={element} />
          ))}
        </Route>

        <Route
          element={
            <TenantStaffAuthProvider>
              <TenantStaffAuthLayout />
            </TenantStaffAuthProvider>
          }
        >
          {tenantStaffAuthRoutes.map(({ path, element }) => (
            <Route key={path} path={path} element={element} />
          ))}
        </Route>

        {/* Catch-alls: any unmatched path falls back to the relevant portal's login */}
        <Route
          path="/admin/*"
          element={<Navigate to={routes.adminLogin} replace />}
        />
        <Route
          path="/tenant-staff/*"
          element={<Navigate to={routes.tenantStaffLogin} replace />}
        />
        <Route path="*" element={<Navigate to={routes.login} replace />} />
      </>
    )
  );

  return (
    <Suspense fallback={<Loader />}>
      <RouterProvider router={router} />
    </Suspense>
  );
};
